import { V2ApiError } from './client';
import { mergeLocationBlob } from './locationCache';
import { findNearestStoreByCoords } from './store';

export const ADDRESS_MATCH_RADIUS_M = 100;

const EARTH_RADIUS_M = 6_371_000;

export function formatAddressLine(addr) {
  if (!addr) return '';
  const parts = [addr.houseDetails, addr.streetDetails, addr.landmark, addr.pincode].filter(Boolean);
  return parts.join(', ');
}

/** Haversine distance in meters between two { lat, lng } points. */
export function distanceMeters(a, b) {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(x));
}

/**
 * Closest saved address within radiusM of userPos, or null.
 */
export function findNearestAddressWithinRadius(userPos, addresses, radiusM = ADDRESS_MATCH_RADIUS_M) {
  if (!userPos || !Array.isArray(addresses) || addresses.length === 0) return null;

  let best = null;
  let bestDist = Infinity;

  for (const addr of addresses) {
    const lat = Number(addr.latLong?.lat);
    const lng = Number(addr.latLong?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    const d = distanceMeters(userPos, { lat, lng });
    if (d <= radiusM && d < bestDist) {
      bestDist = d;
      best = addr;
    }
  }

  return best;
}

export async function reverseGeocode({ lat, lng }) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const json = await res.json();
    const a = json?.address ?? {};
    return (
      a.suburb ||
      a.neighbourhood ||
      a.village ||
      a.town ||
      a.city_district ||
      a.city ||
      a.county ||
      null
    );
  } catch {
    return null;
  }
}

export const OUTSIDE_DELIVERY_RADIUS_MESSAGE =
  'Delivery is not available for this location. It is outside our delivery area.';

function assertDeliverablePodSelection(selection) {
  if (selection?.outsidePods) {
    throw new Error(OUTSIDE_DELIVERY_RADIUS_MESSAGE);
  }

  const store = selection?.store;
  const branchId = store?.branchId ?? store?.branch_id;
  const locCode = store?.locCode ?? store?.loc_code;

  if (!store?.id || branchId == null || branchId === '' || locCode == null || locCode === '') {
    throw new Error(OUTSIDE_DELIVERY_RADIUS_MESSAGE);
  }
}

export async function resolveStoreFromCoords({ lat, lng }) {
  const selection = await findNearestStoreByCoords({ lat, lng });
  assertDeliverablePodSelection(selection);

  const store = selection.store;
  return {
    store,
    storeId: store.id,
    storeName: store.storeName ?? null,
    podId: selection.podId,
  };
}

/**
 * Resolve nearest store for a saved address and merge into v2_location.
 * @returns {{ storeId: string|null }}
 */
export async function resolveAndMergeForAddress(addr) {
  const lat = Number(addr.latLong?.lat);
  const lng = Number(addr.latLong?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error('This address has no map coordinates. Add a new address with a map pin or coordinates.');
  }

  try {
    const { store, storeId, storeName, podId } = await resolveStoreFromCoords({ lat, lng });
    await mergeLocationBlob({
      customerAddress: addr,
      lat,
      lng,
      storeId,
      storeName,
      podId,
      store,
    });
    return { storeId };
  } catch (e) {
    await mergeLocationBlob({
      customerAddress: addr,
      lat,
      lng,
      storeId: null,
      storeName: null,
      podId: null,
      store: null,
    });
    throw e;
  }
}

/**
 * Pick store from GPS: match saved address within 100m when logged in, else GPS-only.
 * @param {{ userPos: { lat: number, lng: number }, addresses?: Array, loggedIn?: boolean, gpsOnly?: boolean }} opts
 *   gpsOnly — explicit "Use current location": never bind a saved address.
 * @returns {Promise<{ mode: 'address'|'gps', storeId: string|null }>}
 */
export async function resolveDeliveryContext({
  userPos,
  addresses = [],
  loggedIn = false,
  gpsOnly = false,
  /** Optional display label from Places search (skips reverse geocode when set). */
  areaLabel = null,
}) {
  if (!userPos) {
    throw new Error('Could not read your location');
  }

  const list = Array.isArray(addresses) ? addresses : [];

  if (!gpsOnly && loggedIn && list.length > 0) {
    const matched = findNearestAddressWithinRadius(userPos, list);
    if (matched) {
      const lat = Number(matched.latLong.lat);
      const lng = Number(matched.latLong.lng);
      const { store, storeId, storeName, podId } = await resolveStoreFromCoords({ lat, lng });
      await mergeLocationBlob({
        customerAddress: matched,
        lat,
        lng,
        storeId,
        storeName,
        podId,
        store,
        areaName: null,
      });
      return { mode: 'address', storeId };
    }
  }

  const { store, storeId, storeName, podId } = await resolveStoreFromCoords(userPos);
  const areaName =
    areaLabel != null && String(areaLabel).trim() !== ''
      ? String(areaLabel).trim()
      : await reverseGeocode(userPos);

  await mergeLocationBlob({
    lat: userPos.lat,
    lng: userPos.lng,
    storeId,
    storeName,
    podId,
    store,
    areaName,
    customerAddress: null,
  });

  return { mode: 'gps', storeId };
}

export function deliveryUnavailableMessage(e) {
  if (e instanceof V2ApiError) return e.message;
  if (e?.message === OUTSIDE_DELIVERY_RADIUS_MESSAGE) return e.message;
  return 'Delivery is not available for this address. It may be outside our delivery area.';
}
